import {
    ErrorHandler,
    NgModule
} from '@angular/core';
import {AppComponent} from './app.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {PreloadAllModules, RouterModule, TitleStrategy} from '@angular/router';
import {MainModule} from './core/main.module';
import {AuthGuard} from './core/security/auth.guard';
import {ViewsModule} from './views/views.module';
import {StoreModule} from '@ngrx/store';
import {ServiceModule} from '@app/core/service/service.module';
import {appReducers, metaReducers} from './app.reducers';
import {IntegrationEffects} from './core/reducers/integration/integration.effects';
import {EffectsModule} from '@ngrx/effects';
import {integrationReducer} from './core/reducers/integration/integration.reducers';
import {AuthEffects} from './views/common/auth.effects';
import {AnnouncementEffects} from './views/common/dialog-announcement/announcement.effects';
import {GlobalErrorHandler} from './core/global-error-handler/global-error-handler.service';
import {StandardPageComponent} from '@app/core/comp/standard-page/standard-page.component';
import {MaintenanceGuard} from '@app/core/security/maintenance.guard';
import {authReducer} from '@app/views/common/auth.reducers';
import {PageNotFoundComponent} from '@app/core/comp/navigation/not-found-page/page-not-found.component';
import {applicationSettingsReducer} from '@app/views/admin/application-settings/store/application-settings.reducers';
import {ApplicationSettingsEffects} from '@app/views/admin/application-settings/store/application-settings.effects';
import {CookieService} from 'ngx-cookie-service';
import {environment} from '@env/environment';
import {AuthInterceptor} from 'angular-auth-oidc-client';
import {HTTP_INTERCEPTORS} from '@angular/common/http';
import {SessionCookieStorage} from '@app/core/security/session-cookie-storage';
import {BrowserStorage} from '@app/core/security/browser-storage';
import {CookieStorage} from '@app/core/security/cookie-storage';
import {BannerNotificationsService} from '@app/core/service/banner-notifications.service';
import {AuthConfigModule} from '@app/core/modules/auth-config.module';
import {MatDialogModule} from '@angular/material/dialog';
import {TemplatePageTitleStrategy} from '@app/core/global/template-page-title-strategy';
// import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
// import {TranslateHttpLoader} from '@ngx-translate/http-loader';

const routes = [
    {
        path: 'search',
        loadChildren: () => import('./views/search/search.module').then(m => m.SearchModule),
        canActivate: [AuthGuard/* , AutoLoginGuard*/]
    },
    {
        path: '',
        component: StandardPageComponent,
        children: [
            {
                path: 'dashboard',
                loadChildren: () => import('./views/dashboard/dashboard.module').then(m => m.DashboardViewModule),
                canActivate: [AuthGuard]
            },
            {
                path: 'administrations',
                loadChildren: () => import('./views/administration/administration.module').then(m => m.AdministrationViewModule),
                canActivate: [AuthGuard/* , AutoLoginGuard*/]
            },
            {
                path: 'admin',
                loadChildren: () => import('./views/admin/admin.module').then(m => m.AdminModule),
                canActivate: [AuthGuard/* , AutoLoginGuard*/]
            },
            {
                path: 'admin/integration',
                loadChildren: () => import('./views/integration/integration.module').then(m => m.IntegrationsModule),
                canActivate: [AuthGuard/* , AutoLoginGuard*/]
            },
            {
                path: 'databrowsing',
                loadChildren: () => import('./views/databrowsing/databrowsing.module').then(m => m.DatabrowsingViewModule),
                canActivate: [AuthGuard/* , AutoLoginGuard*/]
            },
            {
                path: 'settings',
                loadChildren: () => import('./views/system-setting/system-setting.module').then(m => m.SystemSettingModule),
                canActivate: [AuthGuard/* , AutoLoginGuard*/]
            },
            {
                path: 'sso',
                loadChildren: () => import('./views/sso/sso.module').then(m => m.SSOModule),
                canActivate: [AuthGuard/* , AutoLoginGuard*/]
            },
            {
                path: '404',
                name: 'PageNotFound',
                component: PageNotFoundComponent,
                title: 'Page not found'
            },
            {
                path: '**',
                component: PageNotFoundComponent,
                title: 'Page not found'
            }
        ]
    }
];


// AoT requires an exported function for factories
// export function HttpLoaderFactory(http: HttpClient) {
//     return new TranslateHttpLoader(http);
// }


@NgModule({
    declarations: [
        AppComponent,
    ],
    imports: [
        BrowserAnimationsModule,
        MainModule,
        ServiceModule,
        RouterModule.forRoot(routes, {enableTracing: false, preloadingStrategy: PreloadAllModules}),
        // TranslateModule.forRoot({
        //     loader: {
        //         provide: TranslateLoader,
        //         useFactory: HttpLoaderFactory,
        //         deps: [HttpClient]
        //     }
        // }),
        ViewsModule,
        StoreModule.forRoot(appReducers, {
            metaReducers: metaReducers,
            runtimeChecks: {
                strictStateImmutability: false,
                strictActionImmutability: false,
            }
        }),
        StoreModule.forFeature('auth', authReducer),
        StoreModule.forFeature('applicationSettings', applicationSettingsReducer),
        StoreModule.forFeature('integration', integrationReducer),
        EffectsModule.forRoot([AuthEffects]),
        EffectsModule.forFeature([ApplicationSettingsEffects]),
        EffectsModule.forFeature([IntegrationEffects]),
        EffectsModule.forFeature([AnnouncementEffects]),
        // StoreDevtoolsModule.instrumentOnlyWithExtension(),
        AuthConfigModule,
        MatDialogModule
    ],
    exports: [
        BrowserAnimationsModule
    ],
    providers: [
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        {
            provide: TitleStrategy,
            useClass: TemplatePageTitleStrategy
        },
        MaintenanceGuard,
        CookieService,
        BrowserStorage,
        CookieStorage,
        SessionCookieStorage,
        BannerNotificationsService,
        ...(environment.stsServer
            ? [
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: AuthInterceptor,
                    multi: true
                }
            ]
            : []
        )
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}

declare module '@angular/core' {
    interface ModuleWithProviders<T = any> {
        ngModule: Type<T>;
        providers?: (Provider | EnvironmentProviders)[];
    }
}
